import { List, ListItem, SxProps, Typography } from "@mui/material";
import { AthleteForMealPlanTemplateAssignmentFragment } from "apps/web/src/types";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScrollContainer from "../../universal/InfiniteScroll/InfiniteScrollContainer";
import { UseInfiniteCursorConnectionScrollResults } from "../../universal/InfiniteScroll/useInfiniteCursorConnectionScroll";
import { BulkAssignMealPlanTemplateAthleteListItem } from "./BulkAssignMealPlanTemplateAthleteListItem";

const listItemStyle = { width: `calc(100% - ${1})` };

interface AthleteInfiniteScrollProps {
  onClickItem: (athlete: AthleteForMealPlanTemplateAssignmentFragment) => void;
  onClickNoAnthropometryItem: (athlete: AthleteForMealPlanTemplateAssignmentFragment) => void;
  selectedAthleteIds: string[];
  sx?: SxProps;
  searchResults: UseInfiniteCursorConnectionScrollResults<
    "athletesForMealPlanTemplateAssignmentCursorConnection",
    AthleteForMealPlanTemplateAssignmentFragment
  >;
}

export const BulkAssignMealPlanTemplateAthleteInfiniteScroll = ({
  onClickItem,
  onClickNoAnthropometryItem,
  selectedAthleteIds,
  sx,
  searchResults,
}: AthleteInfiniteScrollProps) => {
  const { t } = useTranslation();
  const renderItem = useCallback(
    (athlete: AthleteForMealPlanTemplateAssignmentFragment, forwardRef?: React.MutableRefObject<HTMLDivElement | null>) => {
      return (
        <li key={athlete.id}>
          <div ref={forwardRef}>
            <BulkAssignMealPlanTemplateAthleteListItem
              athlete={athlete}
              onClick={onClickItem}
              onClickNoAnthropometryItem={onClickNoAnthropometryItem}
              sx={listItemStyle} />
          </div>
        </li>
      );
    },
    [onClickItem, onClickNoAnthropometryItem]
  );

  const filterEdges = useCallback(
    (athlete: AthleteForMealPlanTemplateAssignmentFragment) => !selectedAthleteIds.includes(athlete.id),
    [selectedAthleteIds]
  );

  return (
    <List disablePadding sx={sx}>
      <InfiniteScrollContainer
        noItemsComponent={
          <ListItem sx={listItemStyle}>
            <Typography>{t("mealPlanTemplate.bulkAssign.noAthletesFound")}</Typography>
          </ListItem>
        }
        renderItem={renderItem}
        infiniteScrollResults={searchResults}
        loadingOffset={10}
        filterEdges={filterEdges}
      />
    </List>
  );
};
